/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ENVIRONMENT } from 'config';
import { differenceInDays, format } from 'date-fns';
import {
  selectPaceVisualIsDisplay,
  selectRevenueSetIsDisplay,
  selectCxlSetIsDisplay,
  selectHotelFocusOn,
  selectEnableSecondaryHotelGroups,
} from 'modules/dashboard/components/tab-container/summary-tab/selectors';
import { summaryActions } from 'modules/dashboard/components/tab-container/summary-tab/slice';
import {
  selectEnableHotelGroups,
  selectHotelName,
  selectSummaryForecastFilter,
  selectTargetDate,
} from 'modules/dashboard/selectors';
import { DateTime } from 'luxon';
import { DATE_TIME_PATTERN, YEAR_MONTH_DAY_PATTERN } from 'modules/common/constants/date-range';

/**
 * Custom hook to coordinate hotel comparison, visual readiness and extended forecasts
 * @param {String} latestDate - Latest booking date of the selected hotel
 * @param {String} hotelTimezone - Timezone used to evaluate forecast availability
 * @returns {Object} Visual readiness, forecast toggle disabled state and visual load setters
 */
const useSummaryVisualStatus = (latestDate, hotelTimezone) => {
  const dispatch = useDispatch();
  const summaryForecastFilter = useSelector(selectSummaryForecastFilter);
  const targetDate = useSelector(selectTargetDate);
  const paceVisualIsDisplay = useSelector(selectPaceVisualIsDisplay);
  const revenueSetIsDisplay = useSelector(selectRevenueSetIsDisplay);
  const cxlSetIsDisplay = useSelector(selectCxlSetIsDisplay);
  const selectedHotel = useSelector(selectHotelName);
  const hotelFocusOn = useSelector(selectHotelFocusOn);
  const enableSecondaryHotelGroups = useSelector(selectEnableSecondaryHotelGroups);
  const enableHotelGroups = useSelector(selectEnableHotelGroups);
  const [isVisualCard1Loaded, setIsVisualCard1Loaded] = useState(false);
  const [isVisualCard2Loaded, setIsVisualCard2Loaded] = useState(false);
  const [isPaceVisualLoaded, setIsPaceVisualLoaded] = useState(false);
  const [visualToggleDisabled, setVisualToggleDisabled] = useState(false);
  const [isVisualLoading, setIsVisualLoading] = useState(false);
  //
  useEffect(() => {
    const hasDifferentHotels = hotelFocusOn?.id !== selectedHotel?.id;
    const hasDifferentGroupModes =
      Boolean(enableSecondaryHotelGroups) !== Boolean(enableHotelGroups);

    dispatch(
      summaryActions.setIsSummaryHotelComparison(hasDifferentHotels || hasDifferentGroupModes)
    );
  }, [enableHotelGroups, enableSecondaryHotelGroups, selectedHotel, hotelFocusOn]);
  // Triggered when targetDate value changes
  useEffect(() => {
    // Manage state to disable/enable visual toggle
    const lastBookingDateUtc = latestDate
      ? DateTime.fromSQL(format(new Date(latestDate), DATE_TIME_PATTERN), { zone: 'utc' })
      : '';
    const formattedLastBookingDate = latestDate
      ? lastBookingDateUtc.setZone(hotelTimezone).toFormat(YEAR_MONTH_DAY_PATTERN)
      : '';
    if (
      ENVIRONMENT.ALLOW_EXTENDED_FORECASTS?.toLowerCase() === 'true' &&
      differenceInDays(new Date(targetDate.startDate), new Date(targetDate.endDate)) === 0 &&
      differenceInDays(new Date(targetDate.endDate), new Date(formattedLastBookingDate)) >= 28
    ) {
      setVisualToggleDisabled(!summaryForecastFilter);
    } else {
      dispatch(summaryActions.setSummaryVisualToggle(false));
      setVisualToggleDisabled(true);
    }
  }, [targetDate, summaryForecastFilter, latestDate]);
  useEffect(() => {
    // Calculate is Loaded  based on the given conditions
    const pair1 = revenueSetIsDisplay ? revenueSetIsDisplay && isVisualCard1Loaded : true;
    const pair2 = paceVisualIsDisplay ? paceVisualIsDisplay && isPaceVisualLoaded : true;
    const pair6 = cxlSetIsDisplay ? cxlSetIsDisplay && isVisualCard2Loaded : true;
    if (
      revenueSetIsDisplay &&
      isVisualCard1Loaded &&
      paceVisualIsDisplay &&
      isPaceVisualLoaded &&
      cxlSetIsDisplay &&
      isVisualCard2Loaded
    ) {
      setIsVisualLoading(true);
    } else {
      setIsVisualLoading(pair1 || pair2 || pair6);
    }
  }, [
    revenueSetIsDisplay,
    isVisualCard1Loaded,
    paceVisualIsDisplay,
    isPaceVisualLoaded,
    cxlSetIsDisplay,
    isVisualCard2Loaded,
  ]);
  //
  useEffect(() => {
    dispatch(summaryActions.setSummaryVisualToggle(false));
  }, [summaryForecastFilter]);
  return {
    isVisualLoading,
    visualToggleDisabled,
    setIsVisualCard1Loaded,
    setIsVisualCard2Loaded,
    setIsPaceVisualLoaded,
  };
};

export default useSummaryVisualStatus;
